import type { Answers, ClassOverview, ClassWeek } from "../../../shared/api/teaching";

/**
 * D4's stat row, as arithmetic.
 *
 * 🔴 PURE AND OUT OF THE JSX, BECAUSE THESE ARE THE NUMBERS THAT GET REPEATED TO
 * A PARENT. Six tiles' worth of division, subtraction and rounding inline in a
 * component is six things no test can reach without a browser — and every one of
 * them has an edge (a class of nought, a week nobody played, a mean over one
 * sitting) that is wrong in a way that still renders.
 *
 * ⚠️ EVERY FUNCTION HERE REFUSES RATHER THAN GUESSES. A ratio with no
 * denominator comes back `null`, and the screen prints a reason where the
 * numeral would go. `0%` and "nobody answered anything" are different facts and
 * a dashboard that conflates them teaches its reader to ignore it — OD-39's rule
 * (*"no percentage without a denominator anybody can name"*) applied to the six
 * places D4 could most easily break it.
 */

/**
 * A percentage of a stated whole, or null when the whole is nought.
 *
 * ⚠️ THE CALLER PASSES BOTH HALVES AND PRINTS BOTH HALVES. This returns the
 * rounded ratio only; every call site in D4 puts the pair in the caption beside
 * it, so "77%" always has "21 of 27" underneath.
 */
export function share(part: number, whole: number): number | null {
  if (whole <= 0) return null;
  return Math.round((part / whole) * 100);
}

/**
 * How many answers a window has to hold before its accuracy is worth printing.
 *
 * 🔴 THREE RIGHT OUT OF FOUR IS NOT A 75 % CLASS. On the first Monday of a
 * pilot, one child answering four questions would put a confident percentage at
 * the top of every teacher's screen — and a percentage at that size swings
 * twenty points on a single tap, so it would also be visibly different every
 * time she reloaded. `StrengthsPanel` already refuses a per-child verdict below
 * its own floor and calls the refusal *"too early to say"*; this is the class
 * scale of the same judgement.
 *
 * ⚠️ IT IS A FLOOR ON ANSWERS, NOT ON CHILDREN. Twenty answers from one child is
 * as much evidence about the accuracy of the week's play as twenty from twenty —
 * it is a poorer sample of the CLASS, which is why the caption always names how
 * many answers the figure came from rather than implying a headcount.
 */
export const ACCURACY_FLOOR = 20;

/** The window's accuracy as a whole percentage, or null below the floor. */
export function accuracyOf(answers: Answers): number | null {
  if (answers.attempts < ACCURACY_FLOOR) return null;
  return Math.round((answers.correct / answers.attempts) * 100);
}

/**
 * Which way a number moved, and by how much.
 *
 * 🔴 AN ABSOLUTE CHANGE, NOT A PERCENTAGE CHANGE — AND THAT IS A DELIBERATE
 * DEPARTURE FROM THE MOCKUP. *"↑12% from last week"* on 83 sittings is a
 * calculation a teacher has to undo to learn anything ("so… nine more?"), and on
 * a small class it is actively misleading: a class that went from 2 sittings to
 * 3 reads *"↑50%"*, which is a number nobody should ever say out loud about
 * three sittings. *"+9 vs last week"* is the same fact in the unit the tile is
 * already counting in, and it degrades gracefully all the way down to +1.
 *
 * ⚠️ `null` WHEN THERE IS NOTHING TO COMPARE WITH. A class in its first week has
 * no previous window; "+83" against a week that did not exist would read as
 * growth rather than as a start.
 */
export interface Change {
  /** current − previous. Signed; zero is a real and printable answer. */
  amount: number;
  direction: "up" | "down" | "flat";
}

export function changeIn(current: number, previous: number | null): Change | null {
  if (previous === null) return null;
  const amount = current - previous;
  return {
    amount,
    direction: amount > 0 ? "up" : amount < 0 ? "down" : "flat",
  };
}

/**
 * 🔴 A WEEK NOBODY PLAYED IS NOT A BASELINE OF NOUGHT — IT IS NO BASELINE.
 *
 * The subtraction is arithmetically fine either way; the sentence it produces is
 * not. A class that did nothing last week and eleven sittings this week is a
 * class that STARTED, and *"+11 vs last week"* frames a beginning as an
 * improvement over a measured low — which is the reading a head teacher would
 * take into a governors' meeting. So a previous window with no sittings at all
 * yields no comparison, and the tile falls back to saying what the week is.
 */
export function baselineOf(lastWeek: ClassWeek, measure: keyof ClassWeek): number | null {
  if (lastWeek.sessions === 0) return null;
  const value = lastWeek[measure];
  return typeof value === "number" ? value : null;
}

/**
 * The whole row, derived once.
 *
 * ⚠️ ONE FUNCTION RATHER THAN SIX CALLS IN THE JSX, so the tiles cannot drift
 * apart on which window they read. Every field here is either a number the
 * screen prints or a `null` the screen has copy for.
 */
export interface OverviewMetrics {
  /** Children on the register. The denominator for `playedShare`. */
  childCount: number;
  played: number;
  /** Percentage of the register that played this week; null on an empty roll. */
  playedShare: number | null;
  playedChange: Change | null;

  sessions: number;
  sessionsChange: Change | null;

  /** Mean minutes per finished sitting; null when none finished. */
  avgMinutes: number | null;
  /** How many sittings that mean covers, and out of how many. */
  timedSessions: number;

  /** This week's accuracy; null below `ACCURACY_FLOOR` answers. */
  accuracy: number | null;
  answers: Answers;
  accuracyChange: Change | null;

  /**
   * Children on the roll who are on neither attention list.
   *
   * ⚠️ A FLOOR, NOT A CLAIM — and the `Math.max` is why. The register and the
   * attention lists come from different tables, and a sitting can run
   * unrecorded, so the subtraction can in principle outrun the roll.
   */
  goingWell: number;
}

export function metricsOf(o: ClassOverview): OverviewMetrics {
  const { thisWeek, lastWeek } = o;

  /*
   * ⚠️ ACCURACY COMPARES TWO FIGURES THAT BOTH CLEARED THE FLOOR, OR IT DOES NOT
   * COMPARE. A 72 % this week against a 100 % taken from three answers last week
   * is a nine-point "fall" invented out of a sample nobody would defend — worse
   * than showing no arrow, because an arrow is what a teacher acts on.
   */
  const accuracy = accuracyOf(thisWeek.answers);
  const lastAccuracy = accuracyOf(lastWeek.answers);

  return {
    childCount: o.childCount,
    played: thisWeek.activeChildren,
    playedShare: share(thisWeek.activeChildren, o.childCount),
    playedChange: changeIn(
      thisWeek.activeChildren,
      baselineOf(lastWeek, "activeChildren"),
    ),

    sessions: thisWeek.sessions,
    sessionsChange: changeIn(thisWeek.sessions, baselineOf(lastWeek, "sessions")),

    avgMinutes: thisWeek.avgSessionMinutes,
    timedSessions: thisWeek.timedSessions,

    accuracy,
    answers: thisWeek.answers,
    accuracyChange:
      accuracy === null || lastAccuracy === null
        ? null
        : changeIn(accuracy, lastAccuracy),

    goingWell: Math.max(
      0,
      o.childCount -
        o.attention.needsAttention.length -
        o.attention.notStarted.length,
    ),
  };
}
